// src/components/bxpart/BxpartEditV2.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Table,
  Modal,
} from "react-bootstrap";
import { AsyncPaginate } from "react-select-async-paginate";
import Select, { components } from "react-select";
import { authFetch } from "../utils/authFetch";

const LIMIT = 20;

const TIP_OPTIONS = [
  { value: 1, label: "Servicii primite" },
  { value: 2, label: "Obiecte" },
  { value: 3, label: "Servicii prestate" },
];

const HighlightedOption = (props) => {
  const q = (props.selectProps.inputValue || "").trim();
  const label = props.data.label || "";
  const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
  return (
    <components.Option {...props}>
      {parts.map((p, i) =>
        q && p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </components.Option>
  );
};

function useLoader(resource, makeLabel) {
  return useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const skip = (page - 1) * LIMIT;

      try {
        const data = await authFetch(
          `/${resource}?q=${encodeURIComponent(q)}&search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`,
        );

        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.results)
              ? data.results
              : Array.isArray(data?.data)
                ? data.data
                : [];

        const total =
          typeof data?.total === "number"
            ? data.total
            : typeof data?.count === "number"
              ? data.count
              : typeof data?.total_count === "number"
                ? data.total_count
                : undefined;

        const options = items.map((it) => ({
          value: it.id,
          label:
            makeLabel?.(it) ??
            it.denumire ??
            it.name ??
            it.cod ??
            it.code ??
            it.descriere ??
            `#${it.id}`,
          __raw: it,
        }));

        const hasMore =
          typeof total === "number"
            ? skip + items.length < total
            : items.length === LIMIT;

        return { options, hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("loadOptions error", resource, err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resource, makeLabel],
  );
}

export default function BxpartEditV2({ beforeSave } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState(null);

  // sub-tables state for version 2
  const [structManop, setStructManop] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [structProd, setStructProd] = useState([]);

  // Modals state for Manoperă
  const [showManopModal, setShowManopModal] = useState(false);
  const [currentManop, setCurrentManop] = useState(null);
  const [manopForm, setManopForm] = useState({
    manopera_cod: "",
    denumire_manopera: "",
    timp: 0,
    centru_id: "",
    sdv: "",
    masini_utilaje: "",
  });
  const [optCentru, setOptCentru] = useState(null);

  // Modals state for Rețetă
  const [showFinitModal, setShowFinitModal] = useState(false);
  const [currentFinit, setCurrentFinit] = useState(null);
  const [finitForm, setFinitForm] = useState({
    part_id: "",
    um_id: "",
    norma: 0,
    norma500: "",
    customer_id: "",
  });
  const [optMaterialPart, setOptMaterialPart] = useState(null);
  const [optMaterialUm, setOptMaterialUm] = useState(null);

  // Modals state for Preț
  const [showProdModal, setShowProdModal] = useState(false);
  const [currentProd, setCurrentProd] = useState(null);
  const [prodForm, setProdForm] = useState({
    cont_id: "",
    price: 0,
  });
  const [optCont, setOptCont] = useState(null);

  // state pentru selecturile async din form-ul principal
  const [optUM, setOptUM] = useState(null);
  const [optGrupa, setOptGrupa] = useState(null);

  const makeUmLabel = useCallback(
    (u) =>
      [u.cod || u.code || "", u.denumire || u.name || ""]
        .filter(Boolean)
        .join(" — ") || `UM #${u.id}`,
    [],
  );
  const makeGrupaLabel = useCallback(
    (g) =>
      [g.code || g.cod || "", g.denumire || g.name || ""]
        .filter(Boolean)
        .join(" — ") || `Grupă #${g.id}`,
    [],
  );
  const makeCcLabel = useCallback(
    (c) =>
      [c.code || "", c.name || ""]
        .filter(Boolean)
        .join(" — ") || `Centru #${c.id}`,
    [],
  );
  const makeAccountLabel = useCallback(
    (a) =>
      [a.code || "", a.name || ""]
        .filter(Boolean)
        .join(" — ") || `Cont #${a.id}`,
    [],
  );
  const makePartLabel = useCallback(
    (p) =>
      [p.cod || "", p.denumire || ""]
        .filter(Boolean)
        .join(" — ") || `Articol #${p.id}`,
    [],
  );

  const loadUmOptions = useLoader("bxums", makeUmLabel);
  const loadGrupaOptions = useLoader("grupa_produse", makeGrupaLabel);
  const loadCcOptions = useLoader("bxcentrucost", makeCcLabel);
  const loadAccountOptions = useLoader("accounts", makeAccountLabel);
  const loadPartOptions = useLoader("bxpart", makePartLabel);

  const additional = useMemo(() => ({ page: 1 }), []);

  const loadManop = useCallback(async () => {
    try {
      const data = await authFetch(`/bxpart/${id}/structmanop`);
      setStructManop(data || []);
    } catch (e) {
      console.error("Failed to load manopera:", e);
    }
  }, [id]);

  const loadFinit = useCallback(async () => {
    try {
      const data = await authFetch(`/bxreteta/?produs_finit_id=${id}`);
      setRecipes(data || []);
      
      // Update selected recipe if we already have one selected
      if (data && data.length > 0) {
        setSelectedRecipe(prev => {
          if (prev) {
            const updated = data.find(r => r.id === prev.id);
            return updated || data.find(r => r.activa === 1) || data[0];
          }
          return data.find(r => r.activa === 1) || data[0];
        });
      } else {
        setSelectedRecipe(null);
      }
    } catch (e) {
      console.error("Failed to load retete:", e);
    }
  }, [id]);

  const loadProd = useCallback(async () => {
    try {
      const data = await authFetch(`/bxpart/${id}/structprod`);
      setStructProd(data || []);
    } catch (e) {
      console.error("Failed to load preturi:", e);
    }
  }, [id]);

  // încarcă înregistrarea + preselect pentru dropdown-uri
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await authFetch(`/bxpart/${id}`);
        setForm({
          cod: data.cod ?? "",
          denumire: data.denumire ?? "",
          um_id: data.um_id ?? null,
          grupa_produse_id: data.grupa_produse_id ?? null,
          tip_grupa:
            data.tip_grupa_produse ?? data.bxpart_tip_grupa ?? data.grupa_produse?.tip_grupa ?? null,
          cpv: data.cpv ?? "",
          nc8: data.nc8 ?? "",
          ean: data.ean ?? "",
          kg_net: data.kg_net ?? 0,
          kg_brut: data.kg_brut ?? 0,
        });

        // preselect UM (din obiect dacă există)
        if (data?.um?.id) {
          const u = data.um;
          setOptUM({ value: u.id, label: makeUmLabel(u), __raw: u });
          setForm((f) => ({ ...f, um_id: u.id }));
        } else if (data.um_id) {
          try {
            const u = await authFetch(`/bxums/${data.um_id}`);
            setOptUM({ value: u.id, label: makeUmLabel(u), __raw: u });
          } catch {
            setOptUM({ value: data.um_id, label: `UM #${data.um_id}` });
          }
        } else {
          setOptUM(null);
        }

        // preselect Grupă produse
        const grupaId =
          data?.grupa_produse?.id ?? data?.grupa_produse_id ?? null;

        if (grupaId) {
          if (data?.grupa_produse?.id) {
            const g = data.grupa_produse;
            setOptGrupa({ value: g.id, label: makeGrupaLabel(g), __raw: g });
            setForm((f) => ({ ...f, grupa_produse_id: g.id }));
          } else {
            try {
              const g = await authFetch(`/grupa_produse/${grupaId}`);
              setOptGrupa({ value: g.id, label: makeGrupaLabel(g), __raw: g });
            } catch {
              setOptGrupa({ value: grupaId, label: `Grupă #${grupaId}` });
            }
          }
        } else {
          setOptGrupa(null);
        }
      } catch (e) {
        setErr(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, makeUmLabel, makeGrupaLabel]);

  // încarcă datele pentru structuri
  useEffect(() => {
    if (id) {
      loadManop();
      loadFinit();
      loadProd();
    }
  }, [id, loadManop, loadFinit, loadProd]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setSubmitting(true);
      await beforeSave?.();
      const payload = {
        ...form,
        um_id: form.um_id ? Number(form.um_id) : null,
        grupa_produse_id: form.grupa_produse_id
          ? Number(form.grupa_produse_id)
          : null,
        tip_grupa_produse: form.tip_grupa ? Number(form.tip_grupa) : null,
        kg_net: Number(form.kg_net) || 0,
        kg_brut: Number(form.kg_brut) || 0,
      };
      await authFetch(`/bxpart/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      setErr("");
      alert("Datele generale au fost salvate cu succes!");
    } catch (err) {
      setErr("Eroare la salvare: " + (err?.message ?? "unknown"));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Handlers Manoperă ---
  const handleOpenAddManop = () => {
    setCurrentManop(null);
    setManopForm({
      manopera_cod: "",
      denumire_manopera: "",
      timp: 0,
      centru_id: "",
      sdv: "",
      masini_utilaje: "",
    });
    setOptCentru(null);
    setShowManopModal(true);
  };

  const handleOpenEditManop = (item) => {
    setCurrentManop(item);
    setManopForm({
      manopera_cod: item.manopera_cod ?? "",
      denumire_manopera: item.denumire_manopera ?? "",
      timp: item.timp ?? 0,
      centru_id: item.centru_id ?? "",
      sdv: item.sdv ?? "",
      masini_utilaje: item.masini_utilaje ?? "",
    });
    setOptCentru(
      item.centru_id
        ? {
            value: item.centru_id,
            label: item.centru_code && item.centru_name
              ? `${item.centru_code} — ${item.centru_name}`
              : `Centru #${item.centru_id}`,
          }
        : null
    );
    setShowManopModal(true);
  };

  const handleSaveManop = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        part_id: Number(id),
        manopera_cod: manopForm.manopera_cod,
        denumire_manopera: manopForm.denumire_manopera,
        timp: Number(manopForm.timp) || 0,
        centru_id: Number(manopForm.centru_id),
        sdv: manopForm.sdv || null,
        masini_utilaje: manopForm.masini_utilaje || null,
      };

      if (currentManop) {
        await authFetch(`/bxpart/structmanop/${currentManop.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await authFetch(`/bxpart/${id}/structmanop`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setShowManopModal(false);
      loadManop();
    } catch (err) {
      alert("Eroare la salvare manoperă: " + err.message);
    }
  };

  const handleDeleteManop = async (itemId) => {
    if (!window.confirm("Sigur doriți să ștergeți această operație?")) return;
    try {
      await authFetch(`/bxpart/structmanop/${itemId}`, {
        method: "DELETE",
      });
      loadManop();
    } catch (err) {
      alert("Eroare la ștergere manoperă: " + err.message);
    }
  };

  // --- Handlers Rețetă ---
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [recipeForm, setRecipeForm] = useState({
    cod_reteta: "",
    denumire: "",
    data_start: "",
    data_stop: "",
    activa: 1,
    observatii: "",
  });

  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [currentIngredient, setCurrentIngredient] = useState(null);
  const [ingredientForm, setIngredientForm] = useState({
    material_id: "",
    um_id: "",
    cantitate: 0,
    pierdere_proc: 0,
  });

  const handleSelectRecipe = (r) => {
    setSelectedRecipe(r);
  };

  const handleOpenAddRecipe = () => {
    setCurrentRecipe(null);
    setRecipeForm({
      cod_reteta: "",
      denumire: "",
      data_start: new Date().toISOString().substring(0, 10),
      data_stop: "",
      activa: 1,
      observatii: "",
    });
    setShowRecipeModal(true);
  };

  const handleOpenEditRecipe = (r) => {
    setCurrentRecipe(r);
    setRecipeForm({
      cod_reteta: r.cod_reteta,
      denumire: r.denumire,
      data_start: r.data_start ? r.data_start.substring(0, 10) : "",
      data_stop: r.data_stop ? r.data_stop.substring(0, 10) : "",
      activa: r.activa,
      observatii: r.observatii || "",
    });
    setShowRecipeModal(true);
  };

  const handleSaveRecipe = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        produs_finit_id: Number(id),
        cod_reteta: recipeForm.cod_reteta,
        denumire: recipeForm.denumire,
        data_start: recipeForm.data_start + "T00:00:00",
        data_stop: recipeForm.data_stop ? recipeForm.data_stop + "T00:00:00" : null,
        activa: Number(recipeForm.activa),
        observatii: recipeForm.observatii || null,
        linii: [],
      };

      if (currentRecipe) {
        await authFetch(`/bxreteta/${currentRecipe.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await authFetch(`/bxreteta/`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setShowRecipeModal(false);
      await loadFinit();
    } catch (err) {
      alert("Eroare la salvare rețetă: " + err.message);
    }
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (!window.confirm("Sigur doriți să ștergeți această rețetă cu toate materialele ei?")) return;
    try {
      await authFetch(`/bxreteta/${recipeId}`, {
        method: "DELETE",
      });
      await loadFinit();
    } catch (err) {
      alert("Eroare la ștergere rețetă: " + err.message);
    }
  };

  // --- Handlers Materiale Rețetă ---
  const handleOpenAddIngredient = () => {
    setCurrentIngredient(null);
    setIngredientForm({
      material_id: "",
      um_id: "",
      cantitate: 0,
      pierdere_proc: 0,
    });
    setOptMaterialPart(null);
    setOptMaterialUm(null);
    setShowIngredientModal(true);
  };

  const handleOpenEditIngredient = (item) => {
    setCurrentIngredient(item);
    setIngredientForm({
      material_id: item.material_id ?? "",
      um_id: item.um_id ?? "",
      cantitate: item.cantitate ?? 0,
      pierdere_proc: item.pierdere_proc ?? 0,
    });
    setOptMaterialPart(
      item.material_id
        ? {
            value: item.material_id,
            label: item.material_cod_str && item.material_denumire
              ? `${item.material_cod_str} — ${item.material_denumire}`
              : `Material #${item.material_id}`,
          }
        : null
    );
    setOptMaterialUm(
      item.um_id
        ? {
            value: item.um_id,
            label: item.um_denumire ? item.um_denumire : `UM #${item.um_id}`,
          }
        : null
    );
    setShowIngredientModal(true);
  };

  const handleSaveIngredient = async (e) => {
    e.preventDefault();
    if (!selectedRecipe) return;
    try {
      const payload = {
        material_id: Number(ingredientForm.material_id),
        cantitate: Number(ingredientForm.cantitate) || 0,
        um_id: Number(ingredientForm.um_id),
        pierdere_proc: Number(ingredientForm.pierdere_proc) || 0,
      };

      if (currentIngredient) {
        await authFetch(`/bxreteta/linii/${currentIngredient.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await authFetch(`/bxreteta/${selectedRecipe.id}/linii`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setShowIngredientModal(false);
      await loadFinit();
    } catch (err) {
      alert("Eroare la salvare material: " + err.message);
    }
  };

  const handleDeleteIngredient = async (lineId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest material din rețetă?")) return;
    try {
      await authFetch(`/bxreteta/linii/${lineId}`, {
        method: "DELETE",
      });
      await loadFinit();
    } catch (err) {
      alert("Eroare la ștergere material: " + err.message);
    }
  };

  // --- Handlers Prețuri ---
  const handleOpenAddProd = () => {
    setCurrentProd(null);
    setProdForm({
      cont_id: "",
      price: 0,
    });
    setOptCont(null);
    setShowProdModal(true);
  };

  const handleOpenEditProd = (item) => {
    setCurrentProd(item);
    setProdForm({
      cont_id: item.cont_id ?? "",
      price: item.price ?? 0,
    });
    setOptCont(
      item.cont_id
        ? {
            value: item.cont_id,
            label: item.account_code && item.account_name
              ? `${item.account_code} — ${item.account_name}`
              : `Cont #${item.cont_id}`,
          }
        : null
    );
    setShowProdModal(true);
  };

  const handleSaveProd = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        part_id: Number(id),
        cont_id: Number(prodForm.cont_id),
        price: Number(prodForm.price) || 0,
      };

      if (currentProd) {
        await authFetch(`/bxpart/structprod/${currentProd.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await authFetch(`/bxpart/${id}/structprod`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setShowProdModal(false);
      loadProd();
    } catch (err) {
      alert("Eroare la salvare preț: " + err.message);
    }
  };

  const handleDeleteProd = async (itemId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest preț?")) return;
    try {
      await authFetch(`/bxpart/structprod/${itemId}`, {
        method: "DELETE",
      });
      loadProd();
    } catch (err) {
      alert("Eroare la ștergere preț: " + err.message);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <Spinner animation="border" size="sm" /> Se încarcă…
      </Container>
    );
  }
  if (!form) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          {err || "Nu s-a putut încărca înregistrarea."}
        </Alert>
      </Container>
    );
  }

  const renderGeneralFormFields = () => (
    <>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>cod</Form.Label>
            <Form.Control
              type="text"
              name="cod"
              value={form.cod ?? ""}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>denumire</Form.Label>
            <Form.Control
              type="text"
              name="denumire"
              value={form.denumire ?? ""}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>UM</Form.Label>
            <AsyncPaginate
              value={optUM}
              loadOptions={loadUmOptions}
              defaultOptions
              additional={additional}
              onChange={(opt) => {
                setOptUM(opt);
                setForm((f) => ({ ...f, um_id: opt?.value ?? null }));
              }}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Grupă produse</Form.Label>
            <AsyncPaginate
              value={optGrupa}
              loadOptions={loadGrupaOptions}
              defaultOptions
              additional={additional}
              onChange={(opt) => {
                setOptGrupa(opt);
                setForm((f) => ({
                  ...f,
                  grupa_produse_id: opt?.value ?? null,
                  tip_grupa: opt?.__raw?.tip_grupa ?? f.tip_grupa,
                }));
              }}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Tip grupă produse</Form.Label>
            <Select
              classNamePrefix="rs"
              isClearable
              options={TIP_OPTIONS}
              value={
                TIP_OPTIONS.find(
                  (o) => o.value === Number(form?.tip_grupa),
                ) || null
              }
              onChange={(opt) =>
                setForm((f) => ({ ...f, tip_grupa: opt?.value ?? null }))
              }
              placeholder="Alege tipul…"
              components={{ Option: HighlightedOption }}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>cpv</Form.Label>
            <Form.Control
              type="text"
              name="cpv"
              value={form.cpv ?? ""}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>nc8</Form.Label>
            <Form.Control
              type="text"
              name="nc8"
              value={form.nc8 ?? ""}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>ean</Form.Label>
            <Form.Control
              type="text"
              name="ean"
              value={form.ean ?? ""}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>kg_net</Form.Label>
            <Form.Control
              type="number"
              name="kg_net"
              value={form.kg_net ?? 0}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>kg_brut</Form.Label>
            <Form.Control
              type="number"
              name="kg_brut"
              value={form.kg_brut ?? 0}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button variant="secondary" onClick={() => navigate("/bxpart")}>
          Înapoi la listă
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "Se salvează…" : "Salvează"}
        </Button>
      </div>
    </>
  );

  return (
    <Container className="mt-4">
      <Card className="p-4 shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>Editează Bxpart {form?.cod || `#${id}`} (Versiunea 2)</h3>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate("/bxpart")}>
            Înapoi
          </Button>
        </div>
        {err && (
          <Alert variant="danger" className="mt-2">
            {err}
          </Alert>
        )}

        <Tabs defaultActiveKey="generale" id="bxpart-edit-tabs" className="mb-4">
          <Tab eventKey="generale" title="1. Date Generale">
            <Form onSubmit={handleSubmit} className="mt-3">
              {renderGeneralFormFields()}
            </Form>
          </Tab>

          <Tab eventKey="manopera" title="2. Structură Manoperă">
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Operații Manoperă (Template)</h5>
                <Button variant="primary" size="sm" onClick={handleOpenAddManop}>
                  ➕ Adaugă Operație
                </Button>
              </div>

              <Table striped bordered hover responsive size="sm" className="align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Cod</th>
                    <th>Denumire Operație</th>
                    <th>Centru de Cost</th>
                    <th>Timp (ore)</th>
                    <th>SDV</th>
                    <th>Mașini / Utilaje</th>
                    <th className="text-center" style={{ width: "120px" }}>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {structManop.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-3">
                        Nu există operații definite.
                      </td>
                    </tr>
                  ) : (
                    structManop.map((item) => (
                      <tr key={item.id}>
                        <td>{item.manopera_cod}</td>
                        <td>{item.denumire_manopera}</td>
                        <td>
                          {item.centru_code && item.centru_name
                            ? `${item.centru_code} — ${item.centru_name}`
                            : item.centru_code || item.centru_name || `-`}
                        </td>
                        <td>{Number(item.timp).toFixed(2)}</td>
                        <td>{item.sdv || "-"}</td>
                        <td>{item.masini_utilaje || "-"}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-2 py-0 px-2"
                            title="Editează"
                            onClick={() => handleOpenEditManop(item)}
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="py-0 px-2"
                            title="Șterge"
                            onClick={() => handleDeleteManop(item.id)}
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>

          <Tab eventKey="pret" title="3. Structură Preț">
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Structură Prețuri</h5>
                <Button variant="primary" size="sm" onClick={handleOpenAddProd}>
                  ➕ Adaugă Preț
                </Button>
              </div>

              <Table striped bordered hover responsive size="sm" className="align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>Cont</th>
                    <th>Denumire Cont</th>
                    <th>Preț</th>
                    <th className="text-center" style={{ width: "120px" }}>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {structProd.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-3">
                        Nu există prețuri definite.
                      </td>
                    </tr>
                  ) : (
                    structProd.map((item) => (
                      <tr key={item.id}>
                        <td>{item.account_code || "-"}</td>
                        <td>{item.account_name || "-"}</td>
                        <td>{Number(item.price).toFixed(4)}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-2 py-0 px-2"
                            title="Editează"
                            onClick={() => handleOpenEditProd(item)}
                          >
                            ✏️
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="py-0 px-2"
                            title="Șterge"
                            onClick={() => handleDeleteProd(item.id)}
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Tab>
        </Tabs>
      </Card>

      {/* Modal Manoperă */}
      <Modal show={showManopModal} onHide={() => setShowManopModal(false)} size="lg">
        <Form onSubmit={handleSaveManop}>
          <Modal.Header closeButton>
            <Modal.Title>{currentManop ? "Editează Operație" : "Adaugă Operație"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cod Operație</Form.Label>
                  <Form.Control
                    type="text"
                    value={manopForm.manopera_cod}
                    onChange={(e) => setManopForm((f) => ({ ...f, manopera_cod: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Denumire Operație</Form.Label>
                  <Form.Control
                    type="text"
                    value={manopForm.denumire_manopera}
                    onChange={(e) => setManopForm((f) => ({ ...f, denumire_manopera: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Centru de cost</Form.Label>
                  <AsyncPaginate
                    value={optCentru}
                    loadOptions={loadCcOptions}
                    defaultOptions
                    additional={additional}
                    onChange={(opt) => {
                      setOptCentru(opt);
                      setManopForm((f) => ({ ...f, centru_id: opt?.value ?? "" }));
                    }}
                    placeholder="Selectează centru cost..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Timp normat (ore)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={manopForm.timp}
                    onChange={(e) => setManopForm((f) => ({ ...f, timp: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>SDV (Scule, Dispozitive, Verificatoare)</Form.Label>
                  <Form.Control
                    type="text"
                    value={manopForm.sdv}
                    onChange={(e) => setManopForm((f) => ({ ...f, sdv: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mașini / Utilaje</Form.Label>
                  <Form.Control
                    type="text"
                    value={manopForm.masini_utilaje}
                    onChange={(e) => setManopForm((f) => ({ ...f, masini_utilaje: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowManopModal(false)}>
              Anulează
            </Button>
            <Button variant="primary" type="submit">
              Salvează
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Rețetă (Header) */}
      <Modal show={showRecipeModal} onHide={() => setShowRecipeModal(false)} size="md">
        <Form onSubmit={handleSaveRecipe}>
          <Modal.Header closeButton>
            <Modal.Title>{currentRecipe ? "Editează Rețetă" : "Adaugă Rețetă"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cod Rețetă</Form.Label>
                  <Form.Control
                    type="text"
                    value={recipeForm.cod_reteta}
                    onChange={(e) => setRecipeForm((f) => ({ ...f, cod_reteta: e.target.value }))}
                    placeholder="ex: R1"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Denumire</Form.Label>
                  <Form.Control
                    type="text"
                    value={recipeForm.denumire}
                    onChange={(e) => setRecipeForm((f) => ({ ...f, denumire: e.target.value }))}
                    placeholder="ex: Reteta Standard"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Start</Form.Label>
                  <Form.Control
                    type="date"
                    value={recipeForm.data_start}
                    onChange={(e) => setRecipeForm((f) => ({ ...f, data_start: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Stop (Opțional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={recipeForm.data_stop}
                    onChange={(e) => setRecipeForm((f) => ({ ...f, data_stop: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Rețetă Activă"
                    checked={recipeForm.activa === 1}
                    onChange={(e) => setRecipeForm((f) => ({ ...f, activa: e.target.checked ? 1 : 0 }))}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observații</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={recipeForm.observatii}
                    onChange={(e) => setRecipeForm((f) => ({ ...f, observatii: e.target.value }))}
                    placeholder="Alte detalii..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRecipeModal(false)}>
              Anulează
            </Button>
            <Button variant="primary" type="submit">
              Salvează
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Ingredient Rețetă (Lines) */}
      <Modal show={showIngredientModal} onHide={() => setShowIngredientModal(false)} size="lg">
        <Form onSubmit={handleSaveIngredient}>
          <Modal.Header closeButton>
            <Modal.Title>{currentIngredient ? "Editează Material" : "Adaugă Material"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Material / Componentă</Form.Label>
                  <AsyncPaginate
                    value={optMaterialPart}
                    loadOptions={loadPartOptions}
                    defaultOptions
                    additional={additional}
                    onChange={(opt) => {
                      setOptMaterialPart(opt);
                      const raw = opt?.__raw;
                      setIngredientForm((f) => ({
                        ...f,
                        material_id: opt?.value ?? "",
                        um_id: raw?.um_id ?? f.um_id,
                      }));
                      if (raw?.um_id) {
                        setOptMaterialUm({
                          value: raw.um_id,
                          label: raw.um ? makeUmLabel(raw.um) : `UM #${raw.um_id}`,
                        });
                      }
                    }}
                    placeholder="Caută material..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unitate de Măsură</Form.Label>
                  <AsyncPaginate
                    value={optMaterialUm}
                    loadOptions={loadUmOptions}
                    defaultOptions
                    additional={additional}
                    onChange={(opt) => {
                      setOptMaterialUm(opt);
                      setIngredientForm((f) => ({ ...f, um_id: opt?.value ?? "" }));
                    }}
                    placeholder="Selectează U.M..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Cantitate</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={ingredientForm.cantitate}
                    onChange={(e) => setIngredientForm((f) => ({ ...f, cantitate: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Pierdere % (Opțional)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={ingredientForm.pierdere_proc}
                    onChange={(e) => setIngredientForm((f) => ({ ...f, pierdere_proc: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowIngredientModal(false)}>
              Anulează
            </Button>
            <Button variant="primary" type="submit">
              Salvează
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Preț */}
      <Modal show={showProdModal} onHide={() => setShowProdModal(false)}>
        <Form onSubmit={handleSaveProd}>
          <Modal.Header closeButton>
            <Modal.Title>{currentProd ? "Editează Preț" : "Adaugă Preț"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Cont</Form.Label>
                  <AsyncPaginate
                    value={optCont}
                    loadOptions={loadAccountOptions}
                    defaultOptions
                    additional={additional}
                    onChange={(opt) => {
                      setOptCont(opt);
                      setProdForm((f) => ({ ...f, cont_id: opt?.value ?? "" }));
                    }}
                    placeholder="Selectează cont..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Preț</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={prodForm.price}
                    onChange={(e) => setProdForm((f) => ({ ...f, price: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowProdModal(false)}>
              Anulează
            </Button>
            <Button variant="primary" type="submit">
              Salvează
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
